import { useState, useCallback } from 'react';
import { 
  loadLocale, 
  AVAILABLE_LOCALES, 
  getInitialLocale, 
  type Locale 
} from '@/lib/i18n';

export function useLanguage() {
  const [currentLocale, setCurrentLocale] = useState<Locale>(getInitialLocale());
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Switch to a new locale
   */
  const switchLanguage = useCallback(async (locale: Locale) => {
    if (currentLocale === locale) {
      return; // Already active
    }

    setIsLoading(true);
    
    try {
      await loadLocale(locale);
      setCurrentLocale(locale);
    } catch (error) {
      console.error(`Failed to switch to locale ${locale}:`, error);
      // Don't update state if loading failed
    } finally {
      setIsLoading(false);
    }
  }, [currentLocale]);

  return {
    /** Currently active locale */
    current: currentLocale,
    
    /** All available locales */
    available: AVAILABLE_LOCALES,
    
    /** Switch to a different locale */
    switch: switchLanguage,
    
    /** Whether a locale switch is in progress */
    isLoading,
    
    /** Get human-readable locale name */
    getLocaleName: (locale: Locale) => {
      const names: Record<Locale, string> = {
        en: 'English',
        de: 'Deutsch', 
        fr: 'Français',
        it: 'Italiano',
        es: 'Español',
        pt: 'Português'
      };
      return names[locale];
    }
  };
} 